import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Instagram, Loader2, Wrench } from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { ApiError } from '@/lib/api';
import { metaOauthService } from '@/services/metaOauthService';

// All error layouts share the same chrome; this lets one component render the
// typed errors + the catch-all without bloating the JSX tree.
// F116 — error_no_business and error_multiple removed (Instagram Login API).
type ErrorVariant = 'error_denied' | 'error_state_expired' | 'error_transient' | 'error_unknown';

function variantI18nKey(kind: ErrorVariant): string {
  return `onboarding.metaOauth.${kind}`;
}

export function MetaOauthSection() {
  const { t } = useTranslation();
  const { state, markStepCompleted, metaOauthOutcome, setMetaOauthOutcome, refresh } =
    useOnboardingState();
  const [connecting, setConnecting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [stubSubmitting, setStubSubmitting] = useState(false);
  const [stubError, setStubError] = useState<string | null>(null);
  const refreshTimerRef = useRef<number | null>(null);

  async function handleConnect() {
    setStartError(null);
    setConnecting(true);
    try {
      const { url } = await metaOauthService.start();
      // Hand the browser off to Meta; the user returns via `/onboarding/meta/callback`.
      window.location.assign(url);
    } catch (err) {
      setConnecting(false);
      const message =
        err instanceof ApiError ? err.message : t('onboarding.metaOauth.error_unknown.body');
      setStartError(message);
    }
  }

  async function handleDevStub() {
    setStubError(null);
    setStubSubmitting(true);
    try {
      await markStepCompleted('meta_oauth');
    } catch {
      setStubSubmitting(false);
      setStubError(t('onboarding.metaOauth.dev_stub.error'));
    }
  }

  // When the section receives a `connected` outcome (from MetaCallbackPage),
  // auto-refresh the timeline after 2s so the backend's stamp is reflected
  // and the timeline collapses this node + opens the next one.
  useEffect(() => {
    if (metaOauthOutcome.kind !== 'connected') return;
    refreshTimerRef.current = window.setTimeout(() => {
      refresh().catch(() => {
        // Refresh failure leaves the success card on screen — non-fatal.
      });
      setMetaOauthOutcome({ kind: 'idle' });
    }, 2000);
    return () => {
      if (refreshTimerRef.current !== null) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [metaOauthOutcome, refresh, setMetaOauthOutcome]);

  // ----- success card -----
  if (metaOauthOutcome.kind === 'connected') {
    const { ig_username, webhook_subscribed } = metaOauthOutcome.payload;
    const initials =
      ig_username
        .split(/\W+/)
        .filter(Boolean)
        .map((s) => s[0]?.toUpperCase() ?? '')
        .slice(0, 2)
        .join('') || '?';
    return (
      <div className="space-y-4" data-testid="section-meta-oauth">
        <div
          className="flex items-center gap-3 rounded-md border bg-card p-4"
          data-testid="meta-oauth-success-card"
        >
          <Avatar className="h-10 w-10">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">@{ig_username}</p>
            <p className="text-xs text-muted-foreground">
              {t('onboarding.metaOauth.success.subtitle')}
            </p>
          </div>
          <span
            className="inline-flex items-center gap-1 text-success text-sm font-medium"
            aria-label={t('onboarding.metaOauth.success.connected_label')}
          >
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            {t('onboarding.metaOauth.success.connected_label')}
          </span>
        </div>
        {!webhook_subscribed && (
          <div
            className="flex items-start gap-2 rounded-md border border-warning/50 bg-warning/10 p-3 text-sm text-warning-foreground"
            role="status"
            data-testid="meta-oauth-webhook-warning"
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 text-warning" aria-hidden="true" />
            <span>{t('onboarding.metaOauth.success.webhook_warning')}</span>
          </div>
        )}
      </div>
    );
  }

  // ----- error states -----
  const errorVariant: ErrorVariant | null =
    metaOauthOutcome.kind === 'idle' ? null : metaOauthOutcome.kind;

  if (errorVariant) {
    const baseKey = variantI18nKey(errorVariant);
    return (
      <div className="space-y-3" data-testid="section-meta-oauth">
        <div
          className="rounded-md border border-destructive/50 bg-destructive/5 p-4 text-sm"
          role="alert"
          data-testid={`meta-oauth-${errorVariant}-card`}
        >
          <p className="font-medium text-destructive">{t(`${baseKey}.title`)}</p>
          <p className="text-muted-foreground mt-1">{t(`${baseKey}.body`)}</p>
        </div>
        <Button
          type="button"
          onClick={handleConnect}
          disabled={connecting}
          className="min-h-11"
          data-testid="meta-oauth-retry-button"
        >
          {connecting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t('onboarding.metaOauth.connecting')}
            </>
          ) : (
            t('onboarding.metaOauth.idle.cta')
          )}
        </Button>
      </div>
    );
  }

  // ----- dev stub (DEBUG mode, no Meta App credentials) -----
  if (state?.meta_oauth_stub_available && metaOauthOutcome.kind === 'idle') {
    return (
      <div className="space-y-3" data-testid="section-meta-oauth">
        <div
          className="rounded-md border border-warning/50 bg-warning/10 p-4 text-sm text-warning-foreground"
          data-testid="meta-oauth-dev-stub-card"
        >
          <p className="inline-flex items-center gap-1.5 font-medium">
            <Wrench className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('onboarding.metaOauth.dev_stub.title')}
          </p>
          <p className="mt-1 text-muted-foreground">{t('onboarding.metaOauth.dev_stub.body')}</p>
        </div>
        {stubError && (
          <p className="text-sm text-destructive" role="alert">
            {stubError}
          </p>
        )}
        <Button
          type="button"
          onClick={handleDevStub}
          disabled={stubSubmitting}
          className="min-h-11"
          data-testid="meta-oauth-dev-stub-button"
        >
          {stubSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t('onboarding.metaOauth.dev_stub.button')}
            </>
          ) : (
            t('onboarding.metaOauth.dev_stub.button')
          )}
        </Button>
      </div>
    );
  }

  // ----- idle -----
  return (
    <div className="space-y-3" data-testid="section-meta-oauth">
      <p className="text-sm text-muted-foreground">{t('onboarding.metaOauth.idle.body')}</p>
      {startError && (
        <p className="text-sm text-destructive" role="alert">
          {startError}
        </p>
      )}
      <Button
        type="button"
        onClick={handleConnect}
        disabled={connecting}
        className="min-h-11"
        data-testid="meta-oauth-connect-button"
      >
        {connecting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {t('onboarding.metaOauth.connecting')}
          </>
        ) : (
          <>
            <Instagram className="h-4 w-4" aria-hidden="true" />
            {t('onboarding.metaOauth.idle.cta')}
          </>
        )}
      </Button>
    </div>
  );
}
